import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl, Validators } from '@angular/forms';
import { UnRx } from '@pebula/utils';
import { BehaviorSubject } from 'rxjs';

export type SgInputType = 'text' | 'number' | 'password' | 'search';

@Component({
  selector: 'sg-input',
  templateUrl: './sg-input.component.html',
  styleUrls: ['./sg-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
@UnRx()
export class SgInputComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() type: SgInputType = 'text';
  @Input() variant: 'primary' | 'secondary' = 'primary';
  @Input() size: 'small' | 'default' = 'default';
  @Input() width: string = '232px';
  @Input() label: string = 'Label';
  @Input() placeholder: string = null;
  @Input() hideLabel: boolean = false;
  @Input() errorsMap: { [error: 'minlength' | 'maxlength' | 'pattern' | string]: string | RegExp } = {};
  @Input() hint: string = null;
  @Input() maxLimit: number = null;
  @Input() prefix: string = null;
  @Input() prefixIcon: string = null;
  @Input() suffix: string = null;
  @Input() showSuffixAction: boolean = true;
  @Input() clearButton: boolean = false;
  @Input() numberStep: string = '0.1';
  @Input() min: number = null;
  @Input() max: number = null;
  @Input() submitButtonIcon: string = null;
  @Input() submitButtonIsLoading: boolean = false;
  @Input() subscriptSizing: 'fixed' | 'dynamic' = 'fixed';
  @Input() errorFallbackMessage: string = 'Invalid input';

  @Output() valueSubmitted = new EventEmitter<string | number>();
  @Output() searchCleared = new EventEmitter<void>();
  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();

  internalFormControl = new FormControl('');
  showPasswordToggle: boolean = false;
  showHidePasswordIcon: 'visibility_outlined' | 'visibility_off_outlined' = 'visibility_outlined';
  isInputFocused: boolean = false;

  submitButtonIsLoading$ = new BehaviorSubject<boolean>(null);

  get currentInputType(): string {
    if (this.type === 'password') {
      return this.showPasswordToggle ? 'text' : 'password';
    }
    return this.type;
  }

  get isRequired(): boolean {
    return (
      this.ngControl?.control?.hasValidator?.(Validators.required) ||
      this.internalFormControl?.hasValidator(Validators.required) ||
      false
    );
  }

  get valueLength(): number {
    return this.internalFormControl?.value?.length ?? 0;
  }

  get showClearButton(): boolean {
    return (
      this.clearButton &&
      this.internalFormControl?.value?.length > 0 &&
      this.internalFormControl.enabled &&
      !this.submitButtonIcon &&
      this.type === 'text' &&
      !this.showPasswordSuffix
    );
  }

  get showPasswordSuffix(): boolean {
    return this.type === 'password' && this.showSuffixAction && this.internalFormControl.enabled;
  }

  get showSubmitButton(): boolean {
    return this.type === 'text' && !!this.submitButtonIcon && this.internalFormControl.enabled;
  }

  get errorsEntries(): string[] {
    const internalErrors = this.internalFormControl?.errors ? Object.keys(this.internalFormControl.errors) : [];
    const parentErrors = this.ngControl?.control?.errors ? Object.keys(this.ngControl.control.errors) : [];

    // Combine errors from both sources, removing duplicates
    return [...new Set([...internalErrors, ...parentErrors])];
  }

  get hasErrors(): boolean {
    // Check both internal control and parent NgControl
    const internalInvalid =
      this.internalFormControl?.invalid && (this.internalFormControl?.dirty || this.internalFormControl?.touched);
    const parentInvalid =
      this.ngControl?.control?.invalid && (this.ngControl?.control?.dirty || this.ngControl?.control?.touched);

    return internalInvalid || parentInvalid || false;
  }

  get passwordIcon(): string {
    return this.showHidePasswordIcon;
  }

  get shouldShowPrefix(): boolean {
    return !!(this.prefix || this.prefixIcon);
  }

  get shouldShowSuffix(): boolean {
    // Follow design system rules: Suffix + Right Action = NO
    if (this.showClearButton || this.showPasswordSuffix || this.showSubmitButton) {
      return false;
    }
    return !!this.suffix;
  }

  get effectivePlaceholder(): string | null {
    // Hide placeholder when label exists and would overlap with placeholder
    // This happens when input has no value and is not focused (label is not floating)
    if (!this.hideLabel && this.label && !this.internalFormControl.value && !this.isInputFocused) {
      return null;
    }
    return this.placeholder;
  }

  onChange = (_: any) => {};
  onTouched = () => {};

  constructor(
    private elementRef: ElementRef,
    @Optional() @Self() public ngControl: NgControl
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this._registerValidators();

    // Set up form control value changes
    this.internalFormControl.valueChanges.pipe(UnRx(this)).subscribe((value) => {
      this.onChange(value);

      // Also update parent NgControl if it exists (without triggering validation to prevent loops)
      if (this.ngControl?.control && this.ngControl.control.value !== value) {
        this.ngControl.control.setValue(value, { emitEvent: false });
      }
    });

    // Listen for validator changes on parent NgControl (debounced to prevent excessive calls)
    if (this.ngControl?.control) {
      this.ngControl.control.statusChanges.pipe(UnRx(this)).subscribe(() => {
        this._registerValidators();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.submitButtonIsLoading) {
      this.submitButtonIsLoading$.next(changes.submitButtonIsLoading?.currentValue);
    }

    if (changes.width || changes.size) {
      this._setCustomProperties();
    }
  }

  writeValue(value: any): void {
    this.internalFormControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.internalFormControl.disable() : this.internalFormControl.enable();
  }

  togglePasswordVisibility(): void {
    this.showPasswordToggle = !this.showPasswordToggle;
    this.showHidePasswordIcon = this.showPasswordToggle ? 'visibility_off_outlined' : 'visibility_outlined';
  }

  onSearchClear(): void {
    if (this.type === 'search') {
      this.internalFormControl.reset();
      this.searchCleared.emit();
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    if (this.type === 'search' && event.key === 'Enter') {
      this.onSubmit();
    }
  }

  clearInput(): void {
    this.internalFormControl.reset();
    this.focusInput();
  }

  onSubmit(): void {
    if (!this.internalFormControl.invalid && this.internalFormControl.value) {
      this.valueSubmitted.emit(this.internalFormControl.value);
    }
  }

  focusInput(): void {
    const inputElement = this.elementRef.nativeElement.querySelector('input');
    if (inputElement) {
      inputElement.focus();
    }
  }

  onInputBlur(): void {
    this.isInputFocused = false;
    this.onTouched();
    this.blur.emit();

    // Mark parent NgControl as touched if it exists
    if (this.ngControl?.control) {
      this.ngControl.control.markAsTouched();
      this.ngControl.control.updateValueAndValidity();
    }
  }

  onInputFocus(): void {
    this.isInputFocused = true;
    this.focus.emit();
  }

  getErrorMessage(): string {
    // Check parent NgControl errors first
    if (this.ngControl?.control?.errors?.required || this.internalFormControl.errors?.required) {
      return 'Required';
    }

    // Get first error from errorsEntries (includes both internal and parent errors)
    const firstError = this.errorsEntries[0];
    if (firstError && this.errorsMap[firstError]) {
      return this.errorsMap[firstError] as string;
    }

    return this.errorFallbackMessage;
  }

  private _registerValidators(): void {
    // Sync validators from parent NgControl to internal FormControl
    if (this.ngControl?.control && this.ngControl.control.validator !== this.internalFormControl.validator) {
      this.internalFormControl.setValidators(this.ngControl.control.validator);
      this.internalFormControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
  }

  private _setCustomProperties(): void {
    // Set CSS custom properties for dynamic styling
    const element = this.elementRef.nativeElement;
    element.style.setProperty('--sg-input-width', this.width);
    element.style.setProperty('--sg-input-height', this.size === 'small' ? '36px' : '44px');
  }
}
